import { httpClient } from "./client";
import type { RankingApi } from "./interfaces";
// ADR-005: ranking response DTOs come from the generated barrel (single source
// of truth from backend Pydantic), not a hand-written mirror.
import type {
  PlayerRatingResponse,
  PlayerStatsResponse,
  HeadToHeadResponse,
  AchievementResponse,
  PlayerMatchResponse,
  LeaderboardPageResponse,
} from "../types/generated";
import type { MatchResultCreate } from "../types";

export const rankingApi: RankingApi = {
  getMyRating: () => httpClient.get<PlayerRatingResponse>("/ranking/rating"),

  getRating: (userId: string) =>
    httpClient.get<PlayerRatingResponse>(
      `/ranking/rating/${encodeURIComponent(userId)}`,
    ),

  reportResult: (data: MatchResultCreate) =>
    httpClient.post<PlayerMatchResponse>("/ranking/matches/report", data),

  getLeaderboard: (params) => {
    const searchParams = new URLSearchParams();
    if (params?.facility_id)
      searchParams.set("facility_id", params.facility_id);
    if (params?.limit !== undefined)
      searchParams.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      searchParams.set("offset", String(params.offset));
    const qs = searchParams.toString();
    return httpClient.get<LeaderboardPageResponse>(
      `/ranking/leaderboard${qs ? `?${qs}` : ""}`,
    );
  },

  getAchievements: (userId: string) =>
    httpClient.get<AchievementResponse[]>(
      `/ranking/achievements/${encodeURIComponent(userId)}`,
    ),

  // ---- Read-only compete / stats surface (COMPETE slice 5a) ----------------

  getMyStats: () => httpClient.get<PlayerStatsResponse>("/ranking/stats/me"),

  getPlayerStats: (userId: string, facilityId?: string) => {
    const searchParams = new URLSearchParams();
    if (facilityId) searchParams.set("facility_id", facilityId);
    const qs = searchParams.toString();
    return httpClient.get<PlayerStatsResponse>(
      `/ranking/stats/${encodeURIComponent(userId)}${qs ? `?${qs}` : ""}`,
    );
  },

  getHeadToHead: (playerA: string, playerB: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set("player_a", playerA);
    searchParams.set("player_b", playerB);
    return httpClient.get<HeadToHeadResponse>(
      `/ranking/stats/head-to-head?${searchParams.toString()}`,
    );
  },

  listMatches: (params) => {
    const searchParams = new URLSearchParams();
    if (params?.source) searchParams.set("source", params.source);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.offset !== undefined)
      searchParams.set("offset", String(params.offset));
    if (params?.facility_id)
      searchParams.set("facility_id", params.facility_id);
    if (params?.limit !== undefined)
      searchParams.set("limit", String(params.limit));
    const qs = searchParams.toString();
    return httpClient.get<PlayerMatchResponse[]>(
      `/ranking/matches${qs ? `?${qs}` : ""}`,
    );
  },

  getMatch: (matchId: string) =>
    httpClient.get<PlayerMatchResponse>(
      `/ranking/matches/${encodeURIComponent(matchId)}`,
    ),
};
